import React, { useEffect, useState, useRef } from 'react';
import connect from 'Shared/connect';
import { styled } from '@glitz/react';
import { replaceState, scrollTo, URLX, hideSpinner, showSpinner } from '@polarnopyret/scope';
import {
  currentUrl,
  get,
  resolveSyncIfLoadedOrThrow,
} from '@polarnopyret/scope';
import ProductCard from '../ProductCard';
import ProductsListingViewModelType from './IProductsListingViewModel.type';
import FacetViewModel from 'Shared/Facet/FacetViewModel.type';
import FacetValueViewModel from 'Shared/Facet/FacetValueViewModel.type';
import { allSelectedValues } from 'Shared/Facet/toggle-facet';
import { PageType } from 'Shared/State';
import CategoryDataModel from '../../Category/CategoryPage.type';
import { isCompact } from 'Shared/Viewport';
import { SEARCH_QUERY_NAME } from 'Search/action-creators';
import { EpiProperty } from '@polarnopyret/scope-episerver';
import Pagination from 'Shared/SharedComponents/atoms/Pagination';
import ProductSort from 'Shared/SharedComponents/atoms/ProductSort';
import ProductListViewModel from 'Product/ProductListing/ProductListViewModel.type';
import { ButtonHeight, ButtonTheme, SolidButton } from 'Shared/TextButtons';
import { Filter12x12 } from 'Shared/Icons/Filter';
import FilterModal from './FilterModal';
import { createGridClasses } from 'Shared/grid';
import { GridWrap } from 'Shared/PageLayout';
import ActiveFilters, { getActiveFacets } from './ActiveFilters';
import * as style from 'Shared/Style';
import { Dispatch } from 'Shared/State';
import { getCurrentBaseUrl } from 'Shared/utils';
import { updateCanonicalUrl } from 'Shared/reducers';

const ActiveFacets = styled(ActiveFilters, {})

type ConnectedPropType = {
  currentBreakpoint: number;
  headerHeight: number;
  currentPage: PageType;
};

type RequiredPropType = ProductsListingViewModelType & {
  queryParams?: string;
  className?: string;
  title?: string;
  content?: CategoryDataModel;
  setFilterResults?: (value: any) => void;
};

type PropType = RequiredPropType &
  ConnectedPropType & {
    uppercase?: boolean;
    hideFacetAndSort?: boolean;
  };

const Filters = styled.div({
});

const ProductListHeading = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignSelf: 'center',
  width: '100%',
});

const ListHeading = styled.span({
  fontSize: '14px',
  fontWeight: 450,
  lineHeight: '130%',
});

const ProductListTop = styled.div({
  display: 'flex',
  marginBottom: '24px',
});

const RowWrap = styled(GridWrap, {
  marginBottom: '24px'
})

const ItemWrap = styled.div({
  marginBottom: '24px'
})

const ProductListTopWrap = styled(GridWrap, {
  position: 'sticky',
  zIndex: 2,
})

function getWithSpinner(
  url: URLX,
  thenAction: (any: any) => void,
  canonicalUrl?: string,
) {
  return (dispatch: Dispatch) => {
    dispatch(showSpinner());
    return get(url, {})
      .then((r) => r.json())
      .then(thenAction)
      .finally(() => {
        if (canonicalUrl) {
          dispatch(updateCanonicalUrl(canonicalUrl));
        }
        dispatch(hideSpinner());
      });
  };
}

function ProductsListing(props: PropType & {
  getWithSpinner: (
    url: URLX,
    thenAction: (any: any) => void,
    canonicalUrl?: string,
  ) => Promise<void>;
}) {
  const {
    currentBreakpoint,
    content,
    productList,
    headerHeight,
    hideFacetAndSort = false,
    getWithSpinner
  } = props;

  const [showProductSortModal, setShowProductSortModal] = useState(false);
  const [filterModalIsShown, setFilterModalIsShown] = useState(false);
  const [selectedFacets, setSelectedFacets] = useState(productList.facets);
  const [selectedSortBy, setSelectedSortBy] = useState(productList.sortOptions?.value);
  const productListTop = useRef<HTMLDivElement>(null)
  const itemsPerRow = 4;

  useEffect(() => {
    setSelectedFacets(productList.facets);
  }, productList.facets);

  useEffect(() => {
    setSelectedSortBy(productList.sortOptions?.value);
  }, productList.sortOptions?.options);

  function getParsedCurrentUrl() {
    const url = currentUrl();
    url.searchParams.delete('page');
    return url;
  }

  function appendFilterPath(url: URLX) {
    url.pathname = url.pathname + '/filter';
    return url;
  }

  function onRemoveFacet(facet: FacetViewModel, value: FacetValueViewModel | undefined) {
    if (facet.type.toLowerCase() === 'range') {
      const updatedFacets = selectedFacets.map((curr) => {
        if (curr.id === facet.id) {
          curr.maxSelected = curr.max
          curr.minSelected = curr.min
        }
        return curr
      })
      onFacetValueChange(updatedFacets)
      return
    }

    const updatedFacets = selectedFacets.map((currentFacet) => {
      currentFacet.values = currentFacet.values.map((currentValue) => {
        if (value.id === currentValue.id) {
          currentValue.selected = false
        }
        return currentValue
      })
      return currentFacet
    })

    onFacetValueChange(updatedFacets)
  }

  function onFacetValueChange(updatedFacets: FacetViewModel[], ticket: string = '') {
    if (!props.setFilterResults) {
      return;
    }

    const url = getParsedCurrentUrl();

    if (props.queryParams) {
      url.searchParams.set(SEARCH_QUERY_NAME, props.queryParams);
    }

    for (const facet of updatedFacets) {
      url.searchParams.delete(facet.urlName);
      for (const selected of allSelectedValues(facet.values)) {
        url.searchParams.append(facet.urlName, selected.renderAsCheckBox ? selected.selected.toString() : selected.id);
      }

      if (facet.id === 'price' && (facet.min !== facet.minSelected || facet.max !== facet.maxSelected)) {
        url.searchParams.append(facet.urlName, `${facet.minSelected}-${facet.maxSelected}`);
      }
    }

    const addressBarUrl = url.toString();
    getWithSpinner(appendFilterPath(url), (json: ProductListViewModel) => {
      props.setFilterResults(json);
      setSelectedFacets(json.facets);
      replaceState(addressBarUrl, null, false);
    }).then(() => {
      scrollTo(productListTop.current.offsetTop, 500);
    });
  }

  function onResetFacetValues() {
    if (!props.setFilterResults) {
      return;
    }

    const url = getParsedCurrentUrl();
    props.productList.facets.forEach((facet) => {
      url.searchParams.delete(facet.urlName);
    })

    const addressBarUrl = url.toString();
    getWithSpinner(appendFilterPath(url), (json: ProductListViewModel) => {
      props.setFilterResults(json);
      setSelectedFacets(json.facets);
      replaceState(addressBarUrl, null, false);
    }).then(() => {
      scrollTo(productListTop.current.offsetTop, 500);
    });
  }

  const toggleSortModal = () => {
    setShowProductSortModal(!showProductSortModal);
    if (filterModalIsShown && showProductSortModal) {
      setFilterModalIsShown(false);
    }
  };

  const handleProductSortChange = (elm: HTMLInputElement) => {
    const sortBy = elm.value;
    setSelectedSortBy(sortBy);
    setShowProductSortModal(false);
    const url = getParsedCurrentUrl();
    url.searchParams.set('sort', sortBy);
    bindFilterResults(url);
  };

  const navigateToPage = (number: number) => {
    const url = currentUrl();
    url.searchParams.set('page', number.toString());
    const canonicalUrl = getCurrentBaseUrl();
    if (number > 1) {
      canonicalUrl.searchParams.set('page', number.toString());
    }
    bindFilterResults(url, canonicalUrl.toString());
  };

  const bindFilterResults = (url: URLX, canonicalUrl?: string) => {
    if (!props.setFilterResults) {
      return;
    }
    const displayUrl = url.toString();
    // Scroll to top first to keep images from getting into view when scrolling up. Messes up lazy loading
    if (productListTop.current) {
      scrollTo(productListTop.current.offsetTop, 500);
    }

    getWithSpinner(appendFilterPath(url), (json: ProductListViewModel) => {
      props.setFilterResults(json);
      replaceState(displayUrl, null, false);
    }, canonicalUrl).then(() => {

    });
  };

  const totalCount = productList.totalItemCount;
  const visibleCount = productList.products.length;
  const isMobile = isCompact(currentBreakpoint);
  const filterStickyTop = headerHeight + 16;
  const activeFacets: FacetViewModel[] = getActiveFacets(productList.facets)

  const listContentItems = productList.listContentItems && productList.listContentItems.reduce((map, obj) => (map[isMobile ? obj.mobileIndex : obj.desktopIndex] = obj.content?.items[0], map), {});

  const MainContent = content && content.topContentArea && <EpiProperty for={content.topContentArea} />;
  const totalCountTitle = productList.labels?.displayingItems;
  const items: any[] = [];

  for (let i = 0; i < visibleCount; i++) {
    if (listContentItems) {
      const listItem = listContentItems[i + 1];
      if (listItem) {
        const SomeComponent = resolveSyncIfLoadedOrThrow(listItem.componentName);
        items.push(<ItemWrap key={'b' + i} className={createGridClasses(12, 6)}><SomeComponent {...listItem} layout='list' /></ItemWrap>);
      }
    }

    items.push(<ItemWrap key={'i' + i} className={createGridClasses(6, (12 / itemsPerRow))}><ProductCard product={productList.products[i]} listIndex={i} /></ItemWrap>);
  }

  const GalleryContent = (
    <RowWrap className='grid'>
      {items.map((item) => {
        return item;
      })}
    </RowWrap>
  );

  return (
    <div>
      {MainContent}
      <ProductListTopWrap css={{ top: `${filterStickyTop}px` }}>
        <ProductListTop ref={productListTop}>
          {!hideFacetAndSort && (
            <Filters>
              <SolidButton
                onClick={() => { setFilterModalIsShown(true); setShowProductSortModal(false); }}
                theme={ButtonTheme.WhiteToBlack}
                text={productList.labels.filter}
                iconAfter={<Filter12x12 css={{ marginLeft: 8 }} />}
                height={ButtonHeight.Filter}
              />
              <FilterModal
                modalIsShown={filterModalIsShown}
                hideModal={() => setFilterModalIsShown(false)}
                heading={productList.labels.filters}
                activeFiltersLabel={productList.labels.activeFilters ?? 'productList.labels.activeFilters'}
                removeActiveFilter={onRemoveFacet.bind(this)}
                filters={selectedFacets}
                onFiltersUpdated={(facets, ticket) => onFacetValueChange(facets, ticket)}
                onFiltersReset={() => onResetFacetValues()}
                clearFiltersLabel={productList.labels.clearFilter}
                showProductsLabel={productList.labels.showProducts}
              />
            </Filters>
          )}
          {productList.labels &&
            <ProductListHeading>
              <ListHeading>{productList.labels.nrItems}</ListHeading>
            </ProductListHeading>
          }
          {!hideFacetAndSort && (
            <ProductSort
              toggleModal={() => toggleSortModal()}
              handleChange={handleProductSortChange}
              showModal={showProductSortModal}
              label={productList.labels.sort}
              sort={productList.sortOptions}
              selected={selectedSortBy || productList.sortOptions.value}
              isMobile={isMobile}
            />
          )}

        </ProductListTop>
      </ProductListTopWrap>
      {(activeFacets?.length ?? 0) > 0 && <GridWrap><ActiveFacets onRemoveFacet={onRemoveFacet} activeFacets={activeFacets} /></GridWrap>}
      {GalleryContent}
      <GridWrap>
        <Pagination
          currentPage={productList.currentPageIndex}
          currentlyDisplayedItemCount={visibleCount}
          pageCount={productList.totalPages}
          totalItemCount={totalCount}
          totalItemCountLabel={totalCountTitle}
          navigateToPage={navigateToPage}
          totalItemCountTextColor={style.colors.monochrome.a11ygrey}
        />
      </GridWrap>
    </div>
  )
};

export default connect((state) => ({
  currentBreakpoint: state.currentBreakpoint,
  headerHeight: state.UI.headerHeight,
  currentPage: state.currentPage,
}),
  (dispatch) => ({
    getWithSpinner(url: URLX, thenAction: (any: any) => void, canonicalUrl?: string) {
      return dispatch(getWithSpinner(url, thenAction, canonicalUrl));
    },
  }),)(ProductsListing);